import { Form, Formik } from "formik";
import React from "react";
import AutocompleteV2 from "../AutocompleteObjectV2";
import { ConfirmDialog } from "../ConfirmDialog";
import { ConfirmReasonDialog } from "../ConfirmReasonDialog";
import TextField from "../TextField";

interface SelectEditorProps {
    name: string;
    values: any;
    isEditingSelect: boolean;
    editedValue: any;
    showValidationConfirm: boolean;
    showConfirmWithReason?: boolean;
    validationMessage: string;
    reasonKey?: string;
    options?: any[];
    searchFunction?: (searchObject: any) => Promise<any>;
    searchObject?: any;
    getOptionLabel?: (option: any) => string;
    getOptionValue?: (option: any) => any;
    displayValueFunction?: (value: any) => string; // Hàm để hiển thị giá trị khi không edit
    onEdit: () => void;
    onCancel: () => void;
    onSave: (data?: any) => void;
    onConfirmValidation: () => void;
    onCancelValidation: () => void;
    onConfirmWithReason?: () => void;
    onCancelWithReason?: () => void;
    onValueChange: (value: any) => void;
    inline?: boolean; // Hiển thị inline hay xuống hàng khi edit (default: true)
    allowEdit?: boolean;
    allowEditWithoutValue?: boolean;
    placeholder?: string;
    isMulti?: boolean;
    isClearable?: boolean;
    isSearchable?: boolean;
    className?: string;
    dependencies?: any[];
    isSrcoll?: boolean;
    labelSearch?: string;
    urlData?: string;
    viewInputType?: boolean; // Hiển thị dạng input (Autocomplete readonly) thay vì span
    menuPlacement?: "auto" | "top" | "bottom";
}

/**
 * Shared Component để hiển thị và edit select field với Autocomplete
 * Dùng chung cho cả khám bệnh và điều trị
 */
export const SelectEditor: React.FC<SelectEditorProps> = ({
    name = "select",
    values,
    isEditingSelect,
    editedValue,
    showValidationConfirm,
    showConfirmWithReason,
    validationMessage,
    reasonKey = "reason",
    options = [],
    searchFunction,
    searchObject,
    getOptionLabel,
    getOptionValue,
    displayValueFunction,
    onEdit,
    onCancel,
    onSave,
    onConfirmValidation,
    onCancelValidation,
    onConfirmWithReason,
    onCancelWithReason,
    onValueChange,
    inline = true,
    allowEdit = true,
    allowEditWithoutValue = true,
    placeholder = "Chọn...",
    isMulti = false,
    isClearable = true,
    isSearchable = true,
    className,
    dependencies,
    isSrcoll,
    labelSearch,
    urlData,
    viewInputType: isViewInput = false,
    menuPlacement = "auto",
}) => {
    // Hàm hiển thị giá trị
    const renderDisplayValue = () => {
        const value = values?.[name];
        if (!value) return "...";

        if (displayValueFunction) {
            return displayValueFunction(value);
        }

        // Nếu là array (multi-select)
        if (Array.isArray(value)) {
            return value.map(v =>
                getOptionLabel ? getOptionLabel(v) : v.name || v
            ).join(", ");
        }

        // Nếu là object
        if (typeof value === 'object') {
            return getOptionLabel ? getOptionLabel(value) : value.name || "";
        }

        // Nếu là string hoặc number
        return String(value);
    };

    return (
        <Formik initialValues={{}} onSubmit={() => {}}>
            <Form className="w-100">
                {isEditingSelect && inline ? (
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <AutocompleteV2
                            name={name}
                            value={editedValue}
                            options={options}
                            searchFunction={searchFunction}
                            searchObject={searchObject}
                            getOptionLabel={getOptionLabel}
                            getOptionValue={getOptionValue}
                            onChange={(selectedOption: any) => {
                                onValueChange(selectedOption);
                            }}
                            placeholder={placeholder}
                            isMulti={isMulti}
                            isClearable={isClearable}
                            isSearchable={isSearchable}
                            className={className}
                            dependencies={dependencies}
                            isSrcoll={isSrcoll}
                            labelSearch={labelSearch}
                            urlData={urlData}
                            menuPlacement={menuPlacement}
                        />
                        <i
                            className="bi bi-check-circle text-success cursor-pointer"
                            style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                            onClick={() => (showConfirmWithReason ? onConfirmWithReason?.() : onSave?.())}
                            title="Lưu"
                        />
                        <i
                            className="bi bi-x-circle text-danger cursor-pointer"
                            style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                            onClick={onCancel}
                            title="Hủy"
                        />
                    </div>
                ) : !isEditingSelect ? (
                    <>
                        {isViewInput ? (
                            <div className="position-relative flex-grow-1">
                                <TextField
                                    className="input w-100"
                                    name={name}
                                    type="text"
                                    value={renderDisplayValue()}
                                    disabled
                                />
                                {(values?.[name] || allowEditWithoutValue) && allowEdit && (
                                    <i
                                        className="bi bi-pencil-square text-primary cursor-pointer ms-2"
                                        style={{ cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                                        onClick={onEdit}
                                        title="Chỉnh sửa"
                                    />
                                )}
                            </div>
                        ) : (
                            <>
                                <span className="fw-light text-truncate flex-grow-1">
                                    {renderDisplayValue()}
                                </span>
                                {(values?.[name] || allowEditWithoutValue) && allowEdit && (
                                    <i
                                        className="bi bi-pencil-square text-primary cursor-pointer ms-2"
                                        style={{ cursor: 'pointer' }}
                                        onClick={onEdit}
                                        title="Chỉnh sửa"
                                    />
                                )}
                            </>
                        )}
                    </>
                ) : null}

                {/* Nếu inline=false và đang edit, hiển thị AutocompleteV2 ở hàng riêng */}
                {isEditingSelect && !inline && (
                    <div className="d-flex align-items-center gap-2 mb-3 w-100">
                        <AutocompleteV2
                            name={name}
                            value={editedValue}
                            options={options}
                            searchFunction={searchFunction}
                            searchObject={searchObject}
                            getOptionLabel={getOptionLabel}
                            getOptionValue={getOptionValue}
                            onChange={(selectedOption: any) => {
                                onValueChange(selectedOption);
                            }}
                            placeholder={placeholder}
                            isMulti={isMulti}
                            isClearable={isClearable}
                            isSearchable={isSearchable}
                            className={className}
                            dependencies={dependencies}
                            isSrcoll={isSrcoll}
                            labelSearch={labelSearch}
                            urlData={urlData}
                            menuPlacement={menuPlacement}
                        />
                        <i
                            className="bi bi-check-circle text-success cursor-pointer"
                            style={{ fontSize: '1.4rem', cursor: 'pointer' }}
                            onClick={() => (showConfirmWithReason ? onConfirmWithReason?.() : onSave?.())}
                            title="Lưu"
                        />
                        <i
                            className="bi bi-x-circle text-danger cursor-pointer"
                            style={{ fontSize: '1.4rem', cursor: 'pointer' }}
                            onClick={onCancel}
                            title="Hủy"
                        />
                    </div>
                )}

                <ConfirmDialog
                    show={showValidationConfirm}
                    onCloseClick={onCancelValidation}
                    title="Xác nhận"
                    message={validationMessage}
                    onYesClick={onConfirmValidation}
                    onCancelClick={onCancelValidation}
                />

                <ConfirmReasonDialog
                    show={showConfirmWithReason!}
                    close="Hủy"
                    yes="Lưu"
                    onYesClick={(values) => {
                        onSave(values);
                    }}
                    onCloseClick={() => {
                        onCancelWithReason?.();
                    }}
                    title="Xác nhận"
                    name={reasonKey}
                    label="Lý do chỉnh sửa"
                />
            </Form>
        </Formik>
    );
};
